/**
 * `SegmentedControl` — gruppo di pulsanti a selezione singola (segmented /
 * grouped button) del design system.
 *
 * Rende un insieme di opzioni mutuamente esclusive come pulsanti affiancati,
 * uniti in un unico controllo. È l'alternativa "compatta" a un gruppo di radio
 * o a un `<select>` quando le opzioni sono poche (2‑4) e va evidenziata quella
 * attiva.
 *
 * Accessibilità: usa il pattern `radiogroup`/`radio` con `aria-checked`, come
 * `StarRating`/`DotRating`. Fornire sempre un'etichetta accessibile tramite
 * `label` (usata per `aria-label` del gruppo) oppure `aria-label`/
 * `aria-labelledby` via `...rest` non è supportato: passare `label`.
 *
 * È controllato: `value` e `onChange` vivono nel genitore.
 */
import type { ReactNode } from 'react';

/** Singola opzione del controllo. */
export interface SegmentedOption<T extends string> {
  /** Valore associato all'opzione (chiave applicativa). */
  value: T;
  /** Etichetta visibile del pulsante. */
  label: ReactNode;
  /** Icona opzionale mostrata prima dell'etichetta. */
  icon?: ReactNode;
  /** Disabilita la singola opzione. */
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  /** Opzioni mutuamente esclusive, nell'ordine di visualizzazione. */
  options: readonly SegmentedOption<T>[];
  /** Valore attualmente selezionato. */
  value: T;
  /** Notifica il nuovo valore quando l'utente cambia opzione. */
  onChange: (next: T) => void;
  /** Etichetta accessibile del gruppo (`aria-label` del `radiogroup`). */
  label: string;
  /** Disabilita l'intero controllo. */
  disabled?: boolean;
  /** Dimensione: `md` (default) o `sm` (compatta). */
  size?: 'sm' | 'md';
  /**
   * Quando `true` (default) il controllo occupa tutta la larghezza disponibile
   * e le opzioni si dividono equamente lo spazio. Con `false` il controllo si
   * dimensiona sul contenuto (utile come filtro affiancato ad altri elementi).
   */
  fitted?: boolean;
  /**
   * Quando `true`, cliccando l'opzione già attiva la si ri-emette (utile per
   * usare il controllo come filtro commutabile, dove ri-cliccare significa
   * "azzera il filtro"). Con `false` (default) il click su un'opzione già
   * selezionata è ignorato.
   */
  allowDeselect?: boolean;
  /** `id` opzionale del gruppo. */
  id?: string;
  /** Classi aggiuntive sul contenitore. */
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  disabled = false,
  size = 'md',
  fitted = true,
  allowDeselect = false,
  id,
  className,
}: SegmentedControlProps<T>) {
  const rootClasses = [
    'ui-segmented',
    size === 'sm' ? 'ui-segmented--sm' : '',
    fitted ? 'ui-segmented--fitted' : '',
    disabled ? 'ui-segmented--disabled' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div id={id} className={rootClasses} role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const selected = option.value === value;
        const optionDisabled = disabled || option.disabled === true;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`ui-segmented__option${selected ? ' ui-segmented__option--active' : ''}`}
            disabled={optionDisabled}
            onClick={() => {
              if (optionDisabled) return;
              if (!selected || allowDeselect) onChange(option.value);
            }}
          >
            {option.icon != null && (
              <span className="ui-segmented__icon" aria-hidden="true">
                {option.icon}
              </span>
            )}
            <span className="ui-segmented__label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
